/**
 * 法令/RAG資料管理.
 *
 * The corpus is production code in data form: what is in it decides what every
 * clinic's article is judged against, so the screen is shaped around the three
 * questions an operator has to be able to answer.
 *
 *   WHAT IS REGISTERED - the sources, and how recently each was confirmed
 *     current. A guideline last fetched eleven months ago is the risk this list
 *     exists to surface.
 *
 *   WHAT IS LIVE - the corpus releases. One is active; the rest are drafts and
 *     rollbacks. A review records which release it read, so a release is not a
 *     housekeeping detail, it is the thing a finding is accountable to.
 *
 *   DOES RETRIEVAL WORK - the probe. Retrieval quality is otherwise invisible
 *     until it is buried inside a review, where a miss looks like the checker
 *     being lenient rather than like the right provision never having been
 *     found. Asking the corpus a question directly is the only way to see it.
 *
 * Nothing here auto-activates, nothing crawls, and no label ever says 適合.
 */
import { useRef, useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';
import {
  ApiError,
  del,
  patch,
  post,
  postFile,
  type CorpusRelease,
  type CorpusSearchResult,
  type LegalSource,
  type ListResponse,
} from '../api.ts';
import {
  Card,
  Checklist,
  Empty,
  Pill,
  RowLink,
  Table,
  jaDate,
  jaNumber,
  rowOpen,
} from '../components/ui.tsx';
import { InfoButton } from '../components/InfoButton.tsx';
import { ReleaseDetail } from '../components/ReleaseDetail.tsx';
import { Textarea } from '../components/Textarea.tsx';
import { ModalForm } from '../components/Modal.tsx';
import { Field } from '../components/EditPanel.tsx';
import { Select } from '../components/Select.tsx';

interface LoaderData {
  sources: ListResponse<LegalSource> & { allowedHosts: string[]; refetchDays: number };
  releases: ListResponse<CorpusRelease>;
}

type TabId = 'sources' | 'releases' | 'probe';

const KIND_LABEL: Record<string, string> = {
  law: '法令',
  guideline: 'ガイドライン',
  notice: '通知',
  internal: '社内資料',
};

const RELEASE_TONE: Record<string, 'ok' | 'warn' | 'muted'> = {
  ACTIVE: 'ok',
  DRAFT: 'warn',
  ROLLED_BACK: 'muted',
};

const RELEASE_LABEL: Record<string, string> = {
  ACTIVE: '有効',
  DRAFT: '下書き',
  ROLLED_BACK: '旧版',
};

/** How long ago, in the terms an operator judges staleness by. */
/**
 * How long ago this source was last confirmed current, and whether that is a
 * problem.
 *
 * The threshold comes from the server rather than being chosen here, so the
 * screen and the dashboard agree about what counts as stale. Nothing re-fetches
 * automatically - fetching costs a download, an extraction, a re-chunk and an
 * embedding pass - so this is a prompt to a person, which is the point: they
 * know when a ministry has published something.
 */
function freshness(iso: string | null, refetchDays: number): { label: string; stale: boolean } {
  if (iso === null) return { label: '未取得', stale: true };
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return { label: `${jaNumber(days)}日前`, stale: days > refetchDays };
}

export default function AdminLegal() {
  const { sources, releases } = useLoaderData<LoaderData>();
  const revalidator = useRevalidator();

  const [tab, setTab] = useState<TabId>('sources');
  const [adding, setAdding] = useState(false);
  /** The source being corrected, and the one being removed. */
  const [sourceEdit, setSourceEdit] = useState<LegalSource | null>(null);
  /** The release whose contents are open. A release is worth opening. */
  const [openRelease, setOpenRelease] = useState<string | null>(null);
  const [sourceRemove, setSourceRemove] = useState<LegalSource | null>(null);
  const [source, setSource] = useState({
    publisher: '厚生労働省',
    title: '',
    canonicalUrl: '',
    kind: 'guideline',
  });
  /*
   * How the body arrives, asked at registration rather than on a second screen.
   * Registering a name and then going into the source to hand it the document
   * is two steps for one act; the only reason to split them is if the operator
   * has not got the document yet, which is what 'later' is for.
   */
  const [intake, setIntake] = useState<'url' | 'file' | 'later'>('url');
  const [intakeFile, setIntakeFile] = useState<File | null>(null);
  /*
   * The source created by an attempt whose INGESTION then failed - an
   * unreachable URL, an unsupported file. The source itself is fine and the
   * name is already taken, so a retry must not try to create it again.
   */
  const createdSource = useRef<string | null>(null);

  const [query, setQuery] = useState('');
  /** Which draft's standing rules are open for editing, and their text. */
  const [editing, setEditing] = useState<string | null>(null);
  const [digest, setDigest] = useState('');
  const [found, setFound] = useState<CorpusSearchResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const active = releases.items.find((release) => release.status === 'ACTIVE') ?? null;

  const report = (err: unknown, fallback: string) =>
    setError(err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : fallback);

  async function runSearch(event: React.FormEvent) {
    event.preventDefault();
    setBusy('search');
    setError(null);
    try {
      setFound(await post<CorpusSearchResult>('/admin/legal/search', { query: query.trim() }));
    } catch (err) {
      setFound(null);
      report(err, '検索できませんでした');
    } finally {
      setBusy(null);
    }
  }

  async function draftRelease() {
    setBusy('release');
    setError(null);
    setNotice(null);
    try {
      await post('/admin/legal/releases', {});
      setNotice('下書きを作成しました。内容を確認してから有効化してください。');
      await revalidator.revalidate();
    } catch (err) {
      report(err, 'リリースを作成できませんでした');
    } finally {
      setBusy(null);
    }
  }

  async function check(releaseId: string) {
    setBusy(`check-${releaseId}`);
    setError(null);
    setNotice(null);
    try {
      const result = await post<{ status: string; passed: number; total: number; failed: number }>(
        `/admin/legal/releases/${releaseId}/check`,
        {},
      );
      setNotice(
        result.status === 'PASSED'
          ? `検索チェックに合格しました（${result.passed}/${result.total}）。有効化できます。`
          : `検索チェックに失敗しました（不合格 ${result.failed} 件）。どの想定質問が失敗したかを確認してください。`,
      );
      await revalidator.revalidate();
    } catch (err) {
      report(err, 'チェックを実行できませんでした');
    } finally {
      setBusy(null);
    }
  }

  async function saveDigest(releaseId: string) {
    setBusy(`digest-${releaseId}`);
    setError(null);
    setNotice(null);
    try {
      await patch(`/admin/legal/releases/${releaseId}`, { digest });
      setNotice('遵守事項を保存しました。有効化すると、以降の記事生成に反映されます。');
      setEditing(null);
      await revalidator.revalidate();
    } catch (err) {
      report(err, '保存できませんでした');
    } finally {
      setBusy(null);
    }
  }

  async function activate(releaseId: string) {
    setBusy(releaseId);
    setError(null);
    setNotice(null);
    try {
      await post(`/admin/legal/releases/${releaseId}/activate`, {});
      setNotice('有効化しました。以降の法令チェックはこのリリースを参照します。');
      await revalidator.revalidate();
    } catch (err) {
      report(err, '有効化できませんでした');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <Card
        title="法令/RAG資料管理"
        caption={
          active
            ? `現在有効なコーパス: v${active.version_no}（資料 ${active.documents} 件・${jaNumber(active.chunks)} チャンク）`
            : '有効なコーパスリリースがありません。資料を登録し、リリースを作成して有効化してください。'
        }
        actions={
          <>
            {/*
              The rules that govern this screen, behind an icon.

              They were two cards of prose above the material - the guarantees
              about how a document is fetched, and what a verdict label is
              allowed to say. Both matter and both are read once; printed on the
              page they pushed four documents below the fold.
            */}
            <InfoButton title="この画面について">
              <p className="small">
                この画面で登録した資料が、記事の薬機法・医療広告ガイドラインチェックの根拠になります。
                登録しただけでは使われません。<strong>リリースを作成して有効化した資料だけ</strong>が
                チェックに使われます。
              </p>

              <h3 className="infobody__title">取り込みの原則</h3>
              <Checklist
                items={[
                  '取得は隔離されたワーカーで実行し、HTTPS・ホスト許可リスト・リダイレクト再検証・内部アドレス遮断を適用する',
                  '原本をそのまま保管し、発行元・正規URL・公布日・取得日時・SHA-256・パーサ版を記録する',
                  '抽出テキストは常に信頼しないデータとして扱い、モデルへの指示として解釈しない',
                  '現行版との差分を人が確認し、法令ゴールデン評価に合格した場合のみ有効化する',
                  '有効化は1つのコーパスリリースとして原子的に行い、ロールバックを保持する',
                  '関連リリースが変わった記事の審査結果は自動的に「再確認が必要」に戻す',
                ]}
              />

              <h3 className="infobody__title">3つのタブ</h3>
              <dl className="infobody__terms">
                <dt>資料</dt>
                <dd>
                  登録されている文書と、その最終取得日。11か月前に取得したガイドラインを見つけるための
                  一覧です。
                </dd>
                <dt>リリース</dt>
                <dd>
                  <strong>どの資料が「いま使われているか」</strong>を決める単位です。資料を登録・更新
                  しても、リリースを作って有効化するまでチェックの内容は変わりません。有効なリリースは
                  常に1つで、審査結果にはどのリリースを読んだかが記録されるため、あとから「この指摘は
                  どの版の資料に基づくか」を追えます。前の版に戻すこともできます。
                </dd>
                <dt>検索の確認</dt>
                <dd>
                  コーパスに直接質問して、<strong>意図した条文が実際に見つかるか</strong>を確かめる
                  ための画面です。検索が弱いと、チェックは「指摘なし」と答えます - 見つからなかったのか
                  問題が無いのかは、審査結果の中からは区別できません。ここで先に確かめます。
                </dd>
              </dl>

              <h3 className="infobody__title">表示ルール</h3>
              <p className="small">
                判定ラベルは「指摘なし」「要確認」「根拠不足」の3種類のみです。
                <strong>「法令に適合しています」とは表示しません。</strong>
                モデルは提示されたチャンクIDのみを引用でき、引用元と本文の一致はサーバー側で検証します。
              </p>
            </InfoButton>
            <button
              className="btn btn--sm"
              type="button"
              onClick={() => {
                createdSource.current = null;
                setIntakeFile(null);
                setAdding(true);
              }}
            >
              ＋ 資料を登録
            </button>
          </>
        }
      >
        <div className="chips" style={{ marginBottom: 12 }}>
          {(
            [
              ['sources', `資料（${sources.items.length}）`],
              ['releases', `リリース（${releases.items.length}）`],
              ['probe', '検索の確認'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              className="chip"
              aria-pressed={tab === id}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        {notice && !error && (
          <p className="muted small" role="status">
            {notice}
          </p>
        )}

        {tab === 'sources' &&
          (sources.items.length === 0 ? (
            <Empty>
              資料がまだありません。「資料を登録」で発行元と名称を登録し、URLの取得またはPDFの
              アップロードで本文を取り込みます。
            </Empty>
          ) : (
            <Table
              caption={`取得を許可しているホスト: ${sources.allowedHosts.join('、')}`}
              head={
                <>
                  <th scope="col">資料</th>
                  <th scope="col">発行元</th>
                  <th scope="col">区分</th>
                  <th scope="col" className="num">
                    版
                  </th>
                  <th scope="col">最終取得</th>
                  <th scope="col">本文</th>
                  <th scope="col">原本</th>
                  <th scope="col" />
                </>
              }
            >
              {sources.items.map((item) => {
                const fresh = freshness(item.last_fetched_at, sources.refetchDays);
                return (
                  <tr key={item.id} className="row-link">
                    <td>
                      {/*
                        The row is the link. Publisher gets its own column
                        rather than a muted line under the title: it is what an
                        operator scans by - everything 厚生労働省 published, or
                        everything the prefecture did - and a second line inside
                        one cell cannot be scanned down.
                      */}
                      <RowLink to={`/admin/legal/sources/${item.id}`}>{item.title}</RowLink>
                    </td>
                    <td className="muted">{item.publisher}</td>
                    <td>{KIND_LABEL[item.kind] ?? item.kind}</td>
                    <td className="num">{jaNumber(item.versions)}</td>
                    <td>
                      {fresh.stale ? (
                        <Pill tone="warn">{fresh.label}</Pill>
                      ) : (
                        <span className="muted">{fresh.label}</span>
                      )}
                    </td>
                    <td>
                      {/*
                        Whether anything has actually been fetched. A source
                        registered but never ingested looks identical to a live
                        one on a list that only shows names, and it is the one
                        state that means this document is NOT being checked
                        against.
                      */}
                      {item.current_document_id ? (
                        <Pill tone="ok">取込済み</Pill>
                      ) : (
                        <Pill tone="warn">未取込</Pill>
                      )}
                    </td>
                    <td>
                      {item.canonical_url ? (
                        // Untrusted outbound link: no referrer, no opener.
                        <a href={item.canonical_url} target="_blank" rel="noopener noreferrer">
                          開く
                        </a>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      <div className="chips">
                        <button
                          className="btn btn--ghost btn--sm"
                          type="button"
                          onClick={() => setSourceEdit(item)}
                        >
                          編集
                        </button>
                        <button
                          className="btn btn--ghost btn--sm"
                          type="button"
                          onClick={() => setSourceRemove(item)}
                        >
                          削除
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </Table>
          ))}

        {tab === 'releases' && (
          <>
            <div className="editpanel__actions" style={{ marginBottom: 12 }}>
              <button
                className="btn btn--sm"
                type="button"
                disabled={busy !== null}
                onClick={() => void draftRelease()}
              >
                {busy === 'release' ? '作成中…' : '現在の資料からリリースを作成'}
              </button>
            </div>

            {editing && (
              <>
                <p className="form__section">記事生成に渡す遵守事項</p>
                <p className="muted small" style={{ marginTop: 0 }}>
                  短い箇条書きにしてください。これは記事を書く前にモデルへ渡される「常に守る規則」で、
                  条文そのものではありません。書き過ぎると、承認済み情報を載せる余地が狭くなります。
                </p>
                <Textarea
                  minRows={5}
                  maxLength={4000}
                  value={digest}
                  onChange={(event) => setDigest(event.target.value)}
                />
                <div className="editpanel__actions">
                  <button
                    className="btn btn--sm"
                    type="button"
                    disabled={busy !== null}
                    onClick={() => void saveDigest(editing)}
                  >
                    {busy === `digest-${editing}` ? '保存中…' : '保存する'}
                  </button>
                  <button
                    className="btn btn--ghost btn--sm"
                    type="button"
                    onClick={() => setEditing(null)}
                  >
                    やめる
                  </button>
                </div>
              </>
            )}

            {releases.items.length === 0 ? (
              <Empty>リリースがまだありません。</Empty>
            ) : (
              <Table
                head={
                  <>
                    <th scope="col">版</th>
                    <th scope="col">状態</th>
                    <th scope="col" className="num">
                      資料
                    </th>
                    <th scope="col" className="num">
                      チャンク
                    </th>
                    <th scope="col">作成</th>
                    <th scope="col">チェック</th>
                    <th scope="col" />
                  </>
                }
              >
                {releases.items.map((release) => (
                  /*
                    A release is a set of documents, and which documents is the
                    question a finding provokes - so the whole row opens it. The
                    version keeps its own button: it is what the row looks like
                    it is, and a keyboard reaches it either way.
                  */
                  <tr key={release.id} {...rowOpen(() => setOpenRelease(release.id))}>
                    <td>
                      <button
                        className="linkbtn"
                        type="button"
                        onClick={() => setOpenRelease(release.id)}
                      >
                        v{release.version_no}
                      </button>
                      {release.note && (
                        <span className="muted small" style={{ display: 'block' }}>
                          {release.note}
                        </span>
                      )}
                    </td>
                    <td>
                      <Pill tone={RELEASE_TONE[release.status] ?? 'muted'}>
                        {RELEASE_LABEL[release.status] ?? release.status}
                      </Pill>
                    </td>
                    <td className="num">{jaNumber(release.documents)}</td>
                    <td className="num">
                      {jaNumber(release.chunks)}
                      {Number(release.unembedded) > 0 && (
                        <span className="muted small" style={{ display: 'block' }}>
                          {/* Retrieval still works lexically for these. */}
                          埋め込み待ち {jaNumber(release.unembedded)}
                        </span>
                      )}
                      {release.embed_models === 'stub' && (
                        <span className="muted small" style={{ display: 'block' }}>
                          {/* Honest rather than silent: the dense half is hashed
                              n-grams until AI_EMBED_PROVIDER is configured. */}
                          <Pill tone="warn">簡易ベクトル</Pill>
                        </span>
                      )}
                    </td>
                    <td className="muted small">
                      {jaDate(release.created_at)}・{release.created_by_name}
                      {release.activated_at && (
                        <span style={{ display: 'block' }}>
                          有効化 {jaDate(release.activated_at)}・{release.activated_by_name}
                        </span>
                      )}
                    </td>
                    <td>
                      {release.last_check ? (
                        <Pill tone={release.last_check.status === 'PASSED' ? 'ok' : 'danger'}>
                          検索チェック {release.last_check.passed}/{release.last_check.total}
                        </Pill>
                      ) : (
                        <Pill tone="warn">未チェック</Pill>
                      )}
                      {release.last_check?.status === 'FAILED' && (
                        <span className="muted small" style={{ display: 'block' }}>
                          {/* Which question failed, not just that one did. */}
                          {release.last_check.results
                            .filter((result) => !result.passed)
                            .map((result) => `「${result.query}」`)
                            .join('、')}
                        </span>
                      )}
                    </td>
                    <td>
                      {release.status !== 'ACTIVE' && (
                        <div className="editpanel__actions">
                          <button
                            className="btn btn--ghost btn--sm"
                            type="button"
                            disabled={busy !== null}
                            onClick={() => void check(release.id)}
                          >
                            {busy === `check-${release.id}` ? '確認中…' : '検索チェック'}
                          </button>
                          {release.status === 'DRAFT' && (
                            <button
                              className="btn btn--ghost btn--sm"
                              type="button"
                              disabled={busy !== null}
                              onClick={() => {
                                setEditing(editing === release.id ? null : release.id);
                                setDigest(release.digest);
                              }}
                            >
                              遵守事項
                            </button>
                          )}
                          <button
                            className="btn btn--sm"
                            type="button"
                            // Gated in the UI as well as on the server, so the
                            // reason is visible before the refusal.
                            disabled={busy !== null || release.last_check?.status !== 'PASSED'}
                            onClick={() => void activate(release.id)}
                          >
                            {busy === release.id ? '有効化中…' : '有効化'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </>
        )}

        {tab === 'probe' && (
          <>
            <p className="muted small" style={{ marginTop: 0 }}>
              有効なコーパスに対して検索します。記事チェックが参照するのと同じ処理なので、
              「該当する条文が出てくるか」をここで確認できます。
            </p>
            <form className="tenantbar" onSubmit={(event) => void runSearch(event)}>
              <div className="field" style={{ marginBottom: 0, minWidth: 320, flex: 1 }}>
                <label htmlFor="q">確認したい表現</label>
                <input
                  id="q"
                  value={query}
                  maxLength={500}
                  placeholder="例）他院より効果が高い"
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
              <button
                className="btn btn--sm"
                type="submit"
                style={{ alignSelf: 'end' }}
                disabled={busy !== null || query.trim() === ''}
              >
                {busy === 'search' ? '検索中…' : '検索'}
              </button>
            </form>

            {found && (
              <>
                <p className="muted small">
                  コーパス v{found.release.versionNo}・{found.results.length} 件・{found.tookMs}ms
                </p>
                {found.results.length === 0 ? (
                  <Empty>該当する条文が見つかりませんでした。</Empty>
                ) : (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {found.results.map((result) => (
                      <div className="panel" key={result.id}>
                        <p className="muted small" style={{ marginTop: 0 }}>
                          {result.publisher}／{result.title}（第{result.versionNo}版）
                          {'　'}
                          {/* Which half found it: "the corpus is thin" and "the
                              embeddings have not finished" look identical
                              without this. */}
                          {result.lexicalRank !== null && (
                            <Pill tone="muted">語句一致 {result.lexicalRank}位</Pill>
                          )}{' '}
                          {result.denseRank !== null && (
                            <Pill tone="muted">意味一致 {result.denseRank}位</Pill>
                          )}
                        </p>
                        <p style={{ margin: '0 0 4px' }}>
                          <strong>{result.headingPath || '（見出しなし）'}</strong>
                        </p>
                        <p className="small" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                          {result.text}
                        </p>
                        {result.canonicalUrl && (
                          <p className="muted small" style={{ marginBottom: 0 }}>
                            <a href={result.canonicalUrl} target="_blank" rel="noreferrer noopener">
                              原本を開く
                            </a>
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Card>

      {/*
        Correcting a registration, and removing one.

        Neither existed: a mistyped publisher was on every finding that cited
        the source, and a document registered by mistake stayed on the list for
        good. What cannot be edited is anything about a version already
        fetched - the bytes, the digest, the date - because a provenance record
        you can edit is not one.
      */}
      {sourceEdit && (
        <ModalForm
          open
          onClose={() => setSourceEdit(null)}
          title="資料の登録内容を修正"
          caption="取得済みの本文・SHA-256・取得日時は変更できません。"
          submitLabel="保存する"
          onSubmit={async () => {
            await patch(`/admin/legal/sources/${sourceEdit.id}`, {
              publisher: sourceEdit.publisher,
              title: sourceEdit.title,
              canonicalUrl: sourceEdit.canonical_url?.trim() ? sourceEdit.canonical_url.trim() : null,
              kind: sourceEdit.kind,
              notes: sourceEdit.notes,
            });
            setSourceEdit(null);
          }}
        >
          <Field label="発行元" htmlFor="ls-publisher" required>
            <input
              id="ls-publisher"
              value={sourceEdit.publisher}
              maxLength={200}
              onChange={(event) => setSourceEdit({ ...sourceEdit, publisher: event.target.value })}
            />
          </Field>
          <Field label="名称" htmlFor="ls-title" required>
            <input
              id="ls-title"
              value={sourceEdit.title}
              maxLength={300}
              onChange={(event) => setSourceEdit({ ...sourceEdit, title: event.target.value })}
            />
          </Field>
          <Field label="区分" htmlFor="ls-kind">
            <Select
              id="ls-kind"
              value={sourceEdit.kind}
              onChange={(kind) => setSourceEdit({ ...sourceEdit, kind: kind as LegalSource['kind'] })}
              options={Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label }))}
            />
          </Field>
          <Field
            label="正規URL"
            htmlFor="ls-url"
            hint="発行元がこの文書を公開しているページ。指摘の「原本を開く」リンクになります。"
          >
            <input
              id="ls-url"
              type="url"
              placeholder="https://"
              value={sourceEdit.canonical_url ?? ''}
              maxLength={2000}
              onChange={(event) => setSourceEdit({ ...sourceEdit, canonical_url: event.target.value })}
            />
          </Field>
          <Field label="メモ" htmlFor="ls-notes" span="full">
            <Textarea
              id="ls-notes"
              minRows={2}
              maxLength={2000}
              value={sourceEdit.notes}
              onChange={(event) => setSourceEdit({ ...sourceEdit, notes: event.target.value })}
            />
          </Field>
        </ModalForm>
      )}

      {sourceRemove && (
        <ModalForm
          open
          onClose={() => setSourceRemove(null)}
          title="この資料を削除しますか"
          caption={`${sourceRemove.publisher}／${sourceRemove.title}：取得した全ての版と抽出テキストが削除されます。`}
          submitLabel="削除する"
          danger
          onSubmit={async () => {
            await del(`/admin/legal/sources/${sourceRemove.id}`);
            setSourceRemove(null);
          }}
        >
          <p className="field--full" style={{ margin: 0 }}>
            リリースに含まれている資料は削除できません。その場合は、先に該当のリリースを
            ロールバックしてください。
          </p>
        </ModalForm>
      )}

      {openRelease && (
        <ReleaseDetail
          releaseId={openRelease}
          onClose={() => setOpenRelease(null)}
          onChanged={() => void revalidator.revalidate()}
        />
      )}

      <ModalForm
        open={adding}
        onClose={() => setAdding(false)}
        title="資料を登録"
        caption="発行元と名称、そして本文をここでまとめて取り込みます。"
        submitLabel="登録する"
        wide
        onSubmit={async () => {
          if (intake === 'file' && !intakeFile) throw new Error('ファイルを選んでください');
          if (intake === 'url' && source.canonicalUrl.trim() === '') {
            throw new Error('取得するURLを入力してください');
          }

          const id =
            createdSource.current ??
            (
              await post<{ id: string }>('/admin/legal/sources', {
                publisher: source.publisher.trim(),
                title: source.title.trim(),
                kind: source.kind,
                ...(source.canonicalUrl.trim()
                  ? { canonicalUrl: source.canonicalUrl.trim() }
                  : {}),
              })
            ).id;
          createdSource.current = id;

          /*
           * The body is a second write, and it is the one that can fail on
           * something outside our control - a ministry URL that 404s, a file
           * the extractor cannot read. The source stays; the retry above skips
           * re-creating it, and the source's own page can also finish the job.
           */
          if (intake === 'url') {
            await post(`/admin/legal/sources/${id}/fetch`, { url: source.canonicalUrl.trim() });
          } else if (intake === 'file' && intakeFile) {
            await postFile(`/admin/legal/sources/${id}/documents`, intakeFile);
          }
          createdSource.current = null;
        }}
      >
        <Field label="発行元" htmlFor="l-publisher" required>
          <input
            id="l-publisher"
            value={source.publisher}
            maxLength={200}
            required
            onChange={(event) => setSource({ ...source, publisher: event.target.value })}
          />
        </Field>
        <Field label="名称" htmlFor="l-title" required>
          <input
            id="l-title"
            value={source.title}
            maxLength={300}
            required
            placeholder="医療広告ガイドライン"
            onChange={(event) => setSource({ ...source, title: event.target.value })}
          />
        </Field>
        <Field label="区分" htmlFor="l-kind">
          <Select
            id="l-kind"
            value={source.kind}
            onChange={(kind) => setSource({ ...source, kind })}
            options={Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label }))}
          />
        </Field>
        <Field
          label="正規URL"
          htmlFor="l-url"
          span="full"
          hint="発行元の公開ページ。チェック結果に出典として表示され、そのまま開いて原典を確認できます。"
        >
          <input
            id="l-url"
            type="url"
            value={source.canonicalUrl}
            maxLength={500}
            placeholder="https://www.mhlw.go.jp/..."
            onChange={(event) => setSource({ ...source, canonicalUrl: event.target.value })}
          />
        </Field>

        <p className="form__section field--full">本文の取り込み</p>
        <Field label="取り込み方法" htmlFor="l-intake" span="full">
          <Select
            id="l-intake"
            value={intake}
            onChange={(value) => setIntake(value as typeof intake)}
            options={[
              { value: 'url', label: '上の正規URLから取得する' },
              { value: 'file', label: 'ファイルをアップロードする' },
              { value: 'later', label: '登録だけ行い、本文はあとで' },
            ]}
          />
        </Field>

        {intake === 'url' && (
          <p className="muted small field--full" style={{ margin: 0 }}>
            取得はワーカーが行います。許可ホスト: {sources.allowedHosts.join('、')}
          </p>
        )}

        {intake === 'file' && (
          <Field
            label="ファイル"
            htmlFor="l-file"
            span="full"
            hint="PDF・HTML・テキスト。公開URLのない通知などに使います。"
          >
            <input
              id="l-file"
              type="file"
              accept="application/pdf,text/html,text/plain"
              onChange={(event) => setIntakeFile(event.target.files?.[0] ?? null)}
            />
          </Field>
        )}
      </ModalForm>
    </>
  );
}
